/**
 * verse/kernel.ts — Semantic Kernel Extraction
 *
 * Finds the 2-4 word span in player text that carries the most
 * emotional and semantic charge. This kernel is the seed from which
 * the entire response grows — not a template lookup, but a
 * mathematically-identified anchor in the player's own language.
 *
 * Charge = affect_intensity × content_ratio × embedding_distinctiveness
 *
 * The system extracts a primary kernel (highest charge), an optional
 * secondary kernel (non-overlapping), and individual anchor words.
 */

import { embedQuery } from "../neural/embeddings.js";
import { cosine, zeros } from "../neural/math.js";
import { scoreLexicon } from "../neural/affect-lexicon.js";

// ─── Function Words (low semantic charge) ────────────────────────────────────

const FUNCTION_WORDS = new Set([
  // Pronouns
  "i", "me", "my", "mine", "you", "your", "yours", "we", "our", "they", "their",
  "he", "she", "it", "its", "his", "her", "him", "them", "us",
  // Copulas / auxiliaries
  "the", "a", "an", "is", "am", "are", "was", "were", "be", "been", "being",
  "have", "has", "had", "do", "does", "did", "will", "would", "could", "should",
  "can", "may", "might", "shall", "must",
  // Prepositions
  "in", "on", "at", "to", "for", "with", "by", "from", "of", "about", "into",
  "after", "before", "during", "until", "since", "through", "between", "among",
  "away", "back", "down", "off", "over", "around", "along", "aside", "ahead", "apart",
  "together", "behind", "forward", "backward", "toward", "towards", "across", "upon",
  "forth", "front", "inside", "outside", "beneath", "below", "above", "beyond",
  // Conjunctions / relative / interrogative / causal
  "and", "or", "but", "if", "when", "where", "how", "what", "who", "which",
  "that", "this", "these", "those", "than", "whether", "why",
  "because", "since", "although", "though", "unless", "whereas", "versus",
  // Discourse connectors
  "therefore", "thus", "hence", "moreover", "furthermore",
  "nevertheless", "nonetheless", "meanwhile", "otherwise",
  // Adverbs / particles / fillers
  "not", "no", "nor", "so", "as", "up", "out", "just", "like", "even",
  "very", "really", "quite", "rather", "too", "also", "still", "yet",
  "actually", "anyway", "anyways", "basically", "definitely",
  "literally", "maybe", "perhaps", "probably", "seriously", "simply",
  "apparently", "obviously", "clearly", "honestly",
  "kind", "sort", "bit", "lot", "much", "many", "more", "most", "less", "least",
  // Intensifiers / comparatives / superlatives
  "best", "worst", "better", "worse",
  "ever", "never", "always", "sometimes", "often", "already", "lately", "recently",
  "here", "there", "now", "then", "soon", "today", "yesterday", "tomorrow",
  // Contractions
  "don't", "won't", "can't", "didn't", "doesn't", "isn't", "wasn't", "weren't",
  "couldn't", "shouldn't", "wouldn't", "haven't", "hasn't", "hadn't", "aren't",
  "i'm", "i've", "i'd", "i'll", "you're", "you've", "you'd", "you'll",
  "we're", "they're", "it's", "that's", "there's", "here's", "what's",
  "who's", "how's", "he's", "she's",
  // Informal contractions
  "gonna", "gotta", "wanna", "kinda", "sorta",
  // Common verbs (too generic to be kernels)
  "get", "gets", "got", "getting", "going", "goes", "gone", "went", "go",
  "come", "came", "comes", "coming",
  "know", "knows", "knew", "think", "thinks", "thought", "feel", "feels", "feeling", "felt",
  "tell", "telling", "told", "say", "saying", "said",
  "keep", "keeping", "kept", "make", "makes", "making", "made",
  "take", "takes", "taking", "took", "taken", "give", "gives", "giving", "gave", "given",
  "see", "sees", "seeing", "saw", "seen", "mean", "means", "meaning", "meant",
  "want", "wanting", "need", "needing", "try", "trying", "tried",
  "put", "puts", "set", "sets", "run", "runs", "ran", "running",
  "bring", "brings", "brought", "sent", "left",
  "do", "does", "done", "did", "doing",
  "sell", "sells", "sold", "selling",
  "drive", "drives", "drove", "driven", "driving",
  "stand", "stands", "stood", "standing",
  "walk", "walks", "walked", "walking",
  "sit", "sits", "sat", "sitting",
  "understand", "understands", "understood", "understanding",
  "used", "using", "seems", "seemed", "looks", "looked", "looking",
  "started", "stopped", "ended", "began", "continued", "supposed",
  "woke", "woken", "slept", "ate", "eaten", "drank",
  "met", "hit", "cut", "shut", "cost", "quit", "split", "rid", "bid",
  "happened", "called", "asked", "answered", "turned", "moved", "pulled",
  "pushed", "opened", "closed", "picked", "passed", "played", "paid",
  "show", "shows", "showed", "shown", "showing",
  "choose", "chooses", "chose", "chosen", "choosing",
  "speak", "speaks", "spoke", "spoken", "speaking",
  "write", "writes", "wrote", "written", "writing",
  "draw", "draws", "drew", "drawn", "drawing",
  "grow", "grows", "grew", "grown", "growing",
  "wear", "wears", "wore", "worn", "wearing",
  // Past participles only (base forms "fall"/"forget" are in HIGH_VALUE_WORDS)
  "fallen", "forgotten", "gotten",
  "prove", "proves", "proved", "proven", "proving",
  // Polite/social verbs (no emotional anchor)
  "thank", "thanks", "thanked", "thanking", "admire", "admires", "admired", "admiring",
  // Light verbs (action but no semantic weight)
  "let",
  // Indefinites / quantifiers / misc pronouns
  "anything", "everything", "something", "nothing", "someone", "anyone",
  "everyone", "nobody", "somebody", "anybody", "everybody",
  "one", "ones", "two", "once", "twice",
  "wherever", "whenever", "whatever", "however", "whichever",
  "myself", "yourself", "himself", "herself", "itself", "ourselves", "themselves",
  "without", "within", "about",
  // Interjections (NOT content — too light for kernels)
  "hey", "hi", "hello", "yeah", "yep", "nope", "haha", "lol", "lmao",
  "hmm", "hm", "oh", "ah", "ugh", "wow", "okay", "ok", "sure", "right",
  "well", "fine", "alright", "idk", "imo", "tbh", "ngl", "smh", "brb",
  "man", "dude", "bro", "yo",
  // Temporal/manner fillers
  "anymore", "ago", "years", "months", "weeks", "days", "hours", "minutes",
  "year", "month", "week", "day", "hour", "minute", "second", "seconds",
  "time", "times", "while", "moment", "period",
  "morning", "afternoon", "evening", "night", "tonight", "midnight", "noon",
  // Filler adjectives (do NOT include: alone, broken, lost, scared, real, etc.)
  "good", "bad", "great", "nice", "little", "big", "other", "another",
  "some", "any", "each", "every", "own", "same", "different", "all", "both",
  "first", "last", "next", "new", "old", "whole", "single", "long", "short",
  // Vague nouns (no semantic anchor)
  "stuff", "things", "thing", "part", "place", "way", "type",
  // Misc
  "specific", "particular", "certain", "enough", "else", "only",
  "been", "being", "having", "doing",
  // ─── Conversational meta-words (talking about talking) ─────────────────
  "chat", "chatting", "talk", "talking", "conversation", "message",
  "response", "responses", "test", "testing", "tested", "model", "system",
  "version", "update", "question", "answer", "reply", "comment",
  "mention", "mentioned",
  // ─── Expletives as intensifiers (energy without semantic content) ──────
  "fuck", "fucken", "fuckin", "fucking", "shit", "shitty", "damn",
  "damned", "dammit", "hell", "crap", "crappy", "ass", "bullshit",
  "goddamn", "bloody", "freaking", "frickin",
  // ─── Technology / interface meta-discourse ─────────────────────────────
  "app", "program", "computer", "phone", "device", "screen", "click",
  "button", "feature", "bug", "error", "setting", "settings", "option",
  "options",
  // ─── Additional hedges / fillers ──────────────────────────────────────
  "mostly", "pretty", "super", "totally", "completely", "absolutely",
  "certainly", "exactly", "precisely", "guess", "suppose", "figured",
  "reckon", "dunno",
  // ─── Light verbs / adjectives (no emotional anchor) ───────────────────
  // NOTE: "real", "wrong", "funny" are in HIGH_VALUE_WORDS — do NOT add.
  "long", "short", "true", "false", "similar", "reasonable",
  "interesting", "cool", "awesome", "amazing", "wonderful", "fantastic",
  "nice", "neat", "weird", "crazy", "silly", "dumb", "stupid",
  "okay",
  // ─── Adverbs (produce absurd ECHO: "That severely.") ─────────────────
  "severely", "extremely", "incredibly", "remarkably", "significantly",
  "considerably", "substantially", "dramatically", "vastly", "immensely",
  "thoroughly", "deeply", "highly", "greatly", "largely", "widely",
  "heavily", "sharply", "rapidly", "gradually", "slowly", "quickly",
  "barely", "hardly", "slightly", "merely", "purely", "fully",
  "entirely", "utterly", "fundamentally", "essentially", "ultimately",
  "inevitably", "typically", "generally", "occasionally", "frequently",
  "constantly", "continuously", "currently", "previously", "eventually",
  "immediately", "specifically", "particularly", "especially", "primarily",
  // ─── Technical / abstract meta-discourse (no emotional anchor) ────────
  // These leak when player talks ABOUT the system or abstract concepts
  "expanded", "aspects", "intelligence", "orchestration", "architecture",
  "implementation", "functionality", "capability", "mechanism", "component",
  "framework", "infrastructure", "integration", "configuration", "parameter",
  "algorithm", "optimization", "processing", "enhancement", "improvement",
  "modification", "adjustment", "specification", "requirement", "approach",
  "methodology", "strategy", "technique", "perspective", "consideration",
  "factor", "element", "dimension", "category", "concept", "principle",
  "standard", "criteria", "context", "scope", "level", "layer",
  "module", "engine", "pipeline", "workflow", "process", "procedure",
  // ─── Conversational filler verbs ─────────────────────────────────────
  "need", "needs", "needed", "require", "requires", "required",
  "consider", "considered", "considering", "include", "includes", "included",
  "involve", "involves", "involved", "suggest", "suggests", "suggested",
  "indicate", "indicates", "ensure", "ensures", "ensured",
  "provide", "provides", "provided", "support", "supports",
  "maintain", "maintains", "determine", "determines",
  "represent", "represents", "establish", "establishes",
]);

// ─── High-Value Content Words ────────────────────────────────────────────────
// Words that carry strong emotional/semantic charge regardless of affect-lexicon
// coverage. If a span contains any of these, it gets a charge bonus. These are
// never function words — do NOT add words that could be semantic anchors.

const HIGH_VALUE_WORDS = new Set([
  // Emotions / inner states
  "alone", "empty", "broken", "lost", "scared", "afraid", "angry", "hurt",
  "sad", "happy", "lonely", "hopeless", "helpless", "devastated", "numb",
  "hollow", "heavy", "tired", "exhausted", "overwhelmed", "trapped",
  "free", "alive", "dead", "real", "fake", "wrong", "right",
  // Physical/spatial metaphors (also in METAPHOR_WORDS)
  "floor", "ground", "wall", "ceiling", "sky", "dark", "light", "cold",
  "warm", "fire", "water", "storm", "rain", "falling", "sinking",
  "drowning", "floating", "flying", "running", "hiding", "stuck",
  // Relational
  "love", "hate", "trust", "miss", "remember", "forget", "forgive",
  "abandon", "betray", "leave", "stay", "hold",
  // Existential
  "consciousness", "dream", "soul", "death", "life", "meaning",
  "purpose", "truth", "exist", "matter", "point",
  // Actions with weight
  "disappeared", "vanished", "shattered", "crumbled", "collapsed",
  "burned", "drowned", "screamed", "whispered", "cried",
  "passed", "died", "killed", "lost", "broke", "fell", "bled",
  "survived", "endured", "suffered",
  // Domestic / sensory memory (weight in grief, nostalgia, intimacy)
  "recipe", "cooked", "cooking", "tasted", "taste", "smell", "smelled",
  "song", "voice", "photo", "letter", "garden", "kitchen",
  "laughing", "laughed", "smiled", "whispered",
  // Emotional verbs (light in isolation, heavy in context)
  "liked", "loved", "wanted", "wished", "hoped", "needed",
]);

// ─── Metaphor Words ───────────────────────────────────────────────────────────
// Spatial/physical metaphors that morph operators can work with. Subset of
// HIGH_VALUE_WORDS. Spans containing these get an additional charge bonus on
// top of the HIGH_VALUE_WORDS bonus.

const METAPHOR_WORDS = new Set([
  "floor", "ground", "wall", "ceiling", "sky", "falling", "sinking",
  "drowning", "floating", "flying", "running", "hiding", "burning",
  "freezing", "melting", "crumbling", "breaking", "shattered",
  "storm", "rain", "thunder", "fire", "water", "wind",
  "dark", "light", "shadow", "mirror", "glass", "stone",
  "edge", "cliff", "abyss", "void", "ocean", "mountain",
  "prison", "cage", "trap", "hole", "pit", "tunnel",
]);

// ─── Relational Words ────────────────────────────────────────────────────────
// Words that indicate a specific person is being discussed. These carry
// relational weight — "I miss her" is fundamentally different from "I miss
// silence" because there's a human being in the room.

const RELATIONAL_WORDS = new Set([
  "mom", "mother", "dad", "father", "brother", "sister", "son", "daughter",
  "husband", "wife", "partner", "friend", "grandma", "grandmother",
  "grandpa", "grandfather", "baby", "child", "children", "family",
  "uncle", "aunt", "cousin", "nephew", "niece",
  "her", "him", "them", "she", "he",
]);

// ─── Concrete Objects (terrible emotional anchors) ──────────────────────────
// Physical objects that produce absurd responses when nominalized or wrapped:
// "The weight of toilet" / "That mug — it's real" / "Where does glasses take you?"
const CONCRETE_OBJECTS = new Set([
  // Household
  "toilet", "sink", "shower", "bathtub", "faucet", "drain", "pipe",
  "mug", "cup", "glass", "glasses", "plate", "bowl", "fork", "spoon", "knife",
  "pan", "pot", "microwave", "fridge", "refrigerator", "dishwasher",
  "couch", "sofa", "desk", "shelf", "drawer", "cabinet", "counter",
  "carpet", "rug", "curtain", "lamp", "fan", "heater",
  // Electronics
  "phone", "laptop", "computer", "tablet", "charger", "cable", "remote",
  "tv", "television", "speaker", "headphones", "keyboard", "mouse",
  // Vehicle
  "car", "truck", "bus", "train", "bike", "bicycle", "wheel", "tire",
  // Clothing
  "shirt", "pants", "shoes", "socks", "jacket", "coat", "hat", "bag",
  // Nature (concrete, not metaphorical)
  "tree", "bush", "grass", "rock", "dirt", "mud", "leaf", "branch",
  "sidewalk", "street", "road", "parking", "driveway",
  // Building
  "elevator", "escalator", "lobby", "garage", "bathroom",
  // Food (specific items, not abstract "food"/"meal")
  "pizza", "burger", "sandwich", "salad", "soup", "pasta", "rice",
  "bread", "cheese", "chicken", "steak", "egg", "eggs",
  "salt", "sugar", "pepper", "sauce", "butter",
  "banana", "bananas", "apple", "orange",
  // Animals (NOT pets — "cat" and "dog" are companions people tell stories
  // about. "My cat knocked my coffee" is a narrative, not a metaphor.)
  "bird", "fish",
]);

// ─── Light Action Verbs (generic physical actions) ──────────────────────────
// These verbs describe WHAT HAPPENED but not WHY IT MATTERS. In "my cat
// knocked my coffee off the table", the story is cat + coffee, not the
// knocking. VERSE should kernel on nouns, not generic verbs.
// Demotion: -0.10 (lighter than concrete objects because actions can still
// carry weight in emotional contexts: "she knocked on my door at midnight").
const LIGHT_ACTION_VERBS = new Set([
  "knocked", "spilled", "dropped", "tipped", "bumped", "tripped",
  "slipped", "splashed", "poured", "placed", "grabbed", "flipped",
  "tossed", "threw", "caught", "lifted", "carried", "dragged",
  "stacked", "loaded", "unloaded", "packed", "unpacked", "cleaned",
  "wiped", "scrubbed", "swept", "mopped", "folded", "hung", "pinned",
  "clipped", "taped", "glued", "stuck", "fixed", "attached", "removed",
  "cooked", "baked", "fried", "grilled", "boiled", "roasted", "chopped",
  "sliced", "diced", "stirred", "mixed", "blended", "served",
  "ordered", "delivered", "shipped", "mailed", "posted", "printed",
  "parked", "plugged", "charged", "downloaded", "uploaded", "installed",
  // Idiomatic light words: common in casual phrases where they carry no
  // emotional weight. "from scratch" / "not gonna lie" / "vibes" etc.
  // These get a heavier demotion (-0.15) because they're almost always
  // used idiomatically, not as content words.
  "scratch", "vibes", "vibe", "lie",
]);

// ─── Idiomatic Casual Words ──────────────────────────────────────────────────
// Subset of LIGHT_ACTION_VERBS that are almost always used idiomatically
// in casual speech, not as content words. "from scratch" means "from the
// beginning", not about scratching. "not gonna lie" is a discourse marker,
// not about deception. These get heavier demotion (-0.15 vs -0.10).
const IDIOMATIC_CASUAL = new Set([
  "scratch", "vibes", "vibe", "lie",
]);

// ─── Idiomatic Intensifier Patterns ─────────────────────────────────────────
// "best sandwich of my life" → "life" is an INTENSIFIER, not content.
// "worst day of all time" → "time" is an INTENSIFIER, not content.
// HIGH_VALUE_WORDS like "life", "death", "meaning" score +0.20, but when
// they appear in idiomatic superlative constructions, that bonus is wrong.
// The player is talking about the SANDWICH, not about LIFE.
const RE_IDIOMATIC_INTENSIFIER = /\b(?:of my life|of all time|in the world|on earth|in history|i've ever (?:had|seen|done|felt|experienced|tasted|heard|made))\b/i;

// ─── Idiomatic Casual Phrases ───────────────────────────────────────────────
// Phrases where individual words carry no content meaning. "from scratch"
// means "from the beginning" — "scratch" is not about scratching. "not gonna
// lie" is a discourse marker — "lie" is not about deception. When these
// phrases appear, all words in them should be demoted as non-content.
const RE_IDIOMATIC_CASUAL_PHRASE = /\b(?:from scratch|not gonna lie|not going to lie|ngl|no cap|for real though|for real tho|tbh|to be honest|lowkey|highkey|ngl)\b/i;

// Words that appear in idiomatic casual phrases and should be demoted when
// the phrase is detected. These are the content-bearing words of the phrase
// that DON'T carry content in the idiomatic usage.
const IDIOMATIC_CASUAL_PHRASE_WORDS = new Set([
  "scratch", "lie", "cap", "real", "honest", "key",
]);

// ─── Numbers (no emotional charge) ──────────────────────────────────────────
const NUMBERS = new Set([
  "zero", "one", "two", "three", "four", "five", "six", "seven",
  "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen",
  "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty",
  "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
  "hundred", "thousand", "million", "billion",
  "once", "twice",
]);

// ─── Academic / Technical Terms (no emotional charge) ──────────────────────
// Scientific and technical jargon that occasionally appears in player text
// but carries zero emotional weight. "The weight of neurons" is absurd.
const ACADEMIC_WORDS = new Set([
  // Neuroscience / biology
  "neurons", "neural", "synapse", "cortex", "receptor", "genome", "protein",
  "molecule", "atom", "electron", "proton", "neutron", "cell", "membrane",
  // Statistics / math
  "prior", "posterior", "coefficient", "ratio", "frequency", "wavelength",
  "amplitude", "derivative", "integral", "vector", "matrix", "equation",
  "variable", "parameter", "hypothesis", "theory", "analysis",
  // Physics
  "velocity", "momentum", "density", "quantum", "photon", "entropy",
  // Computer science
  "algorithm", "protocol", "interface", "database", "function", "module",
  "binary", "syntax", "compiler", "runtime", "stack", "queue", "hash",
  // General academic
  "methodology", "paradigm", "framework", "correlation", "regression",
  "deviation", "variance", "median", "aggregate", "taxonomy",
]);

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Kernel type — what kind of conversational move the player is making.
 *
 * This is NOT the Aeterna's regulatory strategy (containment/grounding/etc).
 * It's a classification of the player's CONTENT — what they're DOING with
 * language. The planner uses this to select operators appropriate to the
 * content type, rather than relying solely on regulatory strategy.
 *
 * Without kernel types, the planner applies the same operators to "I'm sad"
 * (emotional_state → needs holding) and "I can't stop thinking about it"
 * (rumination → needs compression). Same strategy, wrong operators.
 */
export type KernelType =
  | "emotional_state"     // "I'm sad" / "I'm angry" / "I'm lonely"
  | "emotional_distress"  // "I want to disappear" / "Nobody would care"
  | "relational"          // "I care about you" / "You matter to me"
  | "meta_complaint"      // "That was terrible" / "You're not making sense"
  | "meta_question"       // "Do you actually think?" / "What are you?"
  | "procedural"          // "Okay" / "Yeah" / "No" / "Mhm"
  | "narrative"           // "I went to the store" / "Work was intense"
  | "disclosure"          // "I realized I don't like big groups"
  | "vulnerability"       // "Can I tell you something?" / "I haven't told anyone"
  | "factual"             // "It's currently 2026" / "Today is Monday"
  | "greeting"            // "Hi" / "Hello" / "Hey"
  | "farewell"            // "Bye" / "Goodnight" / "See you later"
  | "identity"            // "I always wanted X" / "I used to be X"
  | "rumination"          // "I keep thinking about" / "I've been wondering"
  | "regret"              // "I wish I had" / "I should have"
  | "transition"          // "Things are changing" / "Everything feels different"
  | "event"               // "My friend came to visit" / "I got promoted"
  | "entity"              // "My friend John" / "She's really cool"
  | "mundane"             // "I'm tired" / "Work was long" / casual state
  | "philosophical"       // "What's the purpose of life?"
  | "playful"             // "What if I just quit?" / hypothetical escape
  | "protest"             // "I'm fine" (denial) / "It doesn't matter"
  | "unknown";            // fallback — nothing matched

export interface Kernel {
  /** The extracted span text */
  span: string;
  /** Individual words */
  words: string[];
  /** Semantic charge 0-1 */
  charge: number;
  /** 64-dim embedding */
  embedding: Float32Array;
  /** Position in original text (0=start, 1=end) */
  position: number;
}

export interface KernelResult {
  /** Highest-charge span */
  primary: Kernel;
  /** Second-highest non-overlapping span, or null */
  secondary: Kernel | null;
  /** Top 1-3 individual content words */
  anchors: string[];
  /** Full text embedding */
  fullEmbedding: Float32Array;
  /** What kind of conversational move the player is making */
  kernelType: KernelType;
}

// ─── Internal ───────────────────────────────────────────────────────────────

interface SpanCandidate {
  text: string;
  words: string[];
  embedding: Float32Array;
  position: number;
  startIdx: number;
  endIdx: number;
  charge: number;
}

/**
 * Extract proper nouns from text BEFORE lowercasing.
 * These are capitalized words not at sentence start and not in a common-caps set.
 * We track them so the kernel scorer can demote them — names aren't emotional anchors.
 */
const PROPER_NOUN_SKIP = new Set([
  "I", "I'm", "I'll", "I've", "I'd",
  "The", "A", "An", "He", "She", "It", "We", "They", "My", "Your",
  "This", "That", "What", "Who", "How", "Why", "When", "Where",
  "Yeah", "Yes", "No", "Hey", "Hello", "Hi", "Okay", "OK",
  "Did", "Do", "Is", "Are", "Was", "Were", "Will", "Would", "Could",
  "But", "And", "Or", "So", "If", "Not", "Also", "Just", "Really",
]);

export function extractProperNounsFromText(text: string): Set<string> {
  const rawWords = text.split(/\s+/);
  const proper = new Set<string>();
  for (let i = 1; i < rawWords.length; i++) {
    const word = rawWords[i].replace(/[.,!?;:'"]+$/g, "");
    if (/^[A-Z][a-z]+$/.test(word) && !PROPER_NOUN_SKIP.has(word)) {
      proper.add(word.toLowerCase());
    }
  }
  return proper;
}

// ─── Typo Normalization ───────────────────────────────────────────────────
// Common misspellings that would break kernel extraction or produce
// nonsensical nominalizations (e.g., "loosing" → "loossing" instead of "losing").
// This is NOT a spell checker — it handles only the most frequent patterns
// that would produce grammatically broken operator output.

const TYPO_MAP: Record<string, string> = {
  // Double-letter errors that change meaning
  "loosing": "losing", "looseing": "losing",
  // Common misspellings of emotional words
  "loosin": "losing",
  "fealing": "feeling",
  "feelin": "feeling",
  "beeing": "being",
  "bein": "being",
  "huring": "hurting",
  "hurin": "hurting",
  "cryin": "crying",
  "dyeing": "dying",
  "lovin": "loving",
  "carin": "caring",
  "hurtin": "hurting",
  "breakin": "breaking",
  "fallin": "falling",
  // Apostrophe issues (common in casual text)
  "dont": "don't",
  "cant": "can't",
  "wont": "won't",
  "isnt": "isn't",
  "wasnt": "wasn't",
  "arent": "aren't",
  "didnt": "didn't",
  "doesnt": "doesn't",
  "couldnt": "couldn't",
  "wouldnt": "wouldn't",
  "shouldnt": "shouldn't",
  "havent": "haven't",
  "hasnt": "hasn't",
  "hadnt": "hadn't",
  "im": "i'm",
  "ive": "i've",
  "youre": "you're",
  "youve": "you've",
  "youd": "you'd",
  "youll": "you'll",
  "theyre": "they're",
  "theres": "there's",
  "heres": "here's",
  "thats": "that's",
  "whats": "what's",
  "whos": "who's",
  "hows": "how's",
  // Common casual spellings
  "tho": "though",
  "thru": "through",
  "nvr": "never",
  "bc": "because",
  "bcuz": "because",
  "cuz": "because",
  "bout": "about",
  "til": "until",
  "rn": "right now",
  "smth": "something",
  "smh": "something",
  "idk": "i don't know",
  "tbh": "honestly",
  "ngl": "honestly",
  "imo": "honestly",
};

function normalizeTypo(word: string): string {
  return TYPO_MAP[word] ?? word;
}

function tokenize(text: string): string[] {
  const raw = text.toLowerCase().match(/[a-z']+/g) ?? [];
  // Expand multi-word normalizations (e.g., "idk" → ["i", "don't", "know"])
  const expanded: string[] = [];
  for (const word of raw) {
    const normalized = normalizeTypo(word);
    // Split on whitespace for multi-word expansions
    const parts = normalized.split(/\s+/);
    expanded.push(...parts);
  }
  return expanded;
}

function contentRatio(words: string[]): number {
  const content = words.filter(w => !FUNCTION_WORDS.has(w)).length;
  return words.length > 0 ? content / words.length : 0;
}

function maxDim(v: Float32Array): number {
  let m = 0;
  for (let i = 0; i < v.length; i++) if (v[i] > m) m = v[i];
  return m;
}

function avgEmbedding(embeddings: Float32Array[]): Float32Array {
  if (embeddings.length === 0) return zeros(64);
  const dim = embeddings[0].length;
  const avg = new Float32Array(dim);
  for (const e of embeddings) for (let i = 0; i < dim; i++) avg[i] += e[i];
  for (let i = 0; i < dim; i++) avg[i] /= embeddings.length;
  return avg;
}

function overlaps(a: SpanCandidate, b: SpanCandidate): boolean {
  return a.startIdx < b.endIdx && b.startIdx < a.endIdx;
}

// ─── Affect-Derived Anchors ──────────────────────────────────────────────────
// When the player says "Okay" or "Yeah" — no content words — the kernel
// extractor finds nothing. But the affect MLP already processed that input
// and produced an 8-dimensional affect vector. That vector IS the seed.
// The dominant dimension tells the planner what emotional shape the filler carries.

const AFFECT_DIM_LABELS = [
  "warmth", "melancholy", "tension", "vigor",
  "curiosity", "playfulness", "surprise", "neutrality",
] as const;

/**
 * Derive an anchor word from the affect vector when no content words exist.
 * Returns the label of the dominant affect dimension (e.g., "warmth").
 * The operators know how to work with these affect anchors:
 *   ECHO on "warmth" → "That warmth." (acknowledging the warm shape of their "Okay")
 *   ECHO_TOGETHER on "warmth" → "I'm with you." (presence matching the warmth)
 *   TEMPORAL_HOLD on "melancholy" → "I'm staying." (holding the sad shape of their "Okay")
 */
function deriveAffectAnchor(affectVector: Float32Array): string {
  let maxIdx = 0;
  let maxVal = affectVector[0];
  for (let i = 1; i < affectVector.length && i < AFFECT_DIM_LABELS.length; i++) {
    if (affectVector[i] > maxVal) {
      maxVal = affectVector[i];
      maxIdx = i;
    }
  }
  return AFFECT_DIM_LABELS[maxIdx];
}

// ─── Kernel Type Classification ───────────────────────────────────────────────

/**
 * Context for kernel type classification — a thin slice of cortex state.
 * Optional: when not provided, classification falls back to text-pattern only.
 */
export interface ClassifyContext {
  /** Parsed intent type from the grammar module */
  intentType?: string;
  /** Dialogue topic slot */
  topic?: string;
  /** Dialogue intent slot */
  dialogueIntent?: string;
  /** Has the semantics engine detected a contradiction? */
  hasContradiction?: boolean;
  /** What type of contradiction (denial/minimizing/deflecting/performing)? */
  contradictionType?: string;
  /** Has the semantics engine detected a metaphor? */
  hasMetaphor?: boolean;
  /** Overall emotional intensity (0-1) */
  intensity?: number;
  /** Melancholy dimension (0-1) */
  melancholy?: number;
  /** Warmth dimension (0-1) */
  warmth?: number;
  /** Tension dimension (0-1) */
  tension?: number;
  /** Depth dimension (0-1) */
  depth?: number;
  /** Is the player disengaging? */
  isDisengaging?: boolean;
  /** 8-dim affect vector for affect-derived fallback */
  affectVector?: Float32Array;
}

// ── Distress markers — high-impact emotional words that signal crisis ──
const DISTRESS_MARKERS = new Set([
  "disappear", "vanish", "cease", "die", "dead", "kill", "suicide",
  "numb", "empty", "hollow", "nothing", "hopeless", "helpless",
  "desperate", "overwhelmed", "terrified", "broken", "shattered",
  "falling", "apart", "end", "gone", "worthless", "pointless",
]);

// ── Relational markers — words that signal the player is talking about a bond ──
const RELATIONAL_MARKERS = new Set([
  "love", "miss", "care", "need", "trust", "important", "matter",
  "cherish", "adore", "belong", "together", "forever", "always",
  "yours", "mine", "ours", "heart", "soul", "dear",
]);

// ── Meta complaint markers — player frustrated with the system ──
const META_COMPLAINT_MARKERS = new Set([
  "terrible", "awful", "horrible", "wrong", "broken", "bad",
  "stupid", "useless", "frustrating", "annoying", "rude",
  "doesn't make sense", "not making sense", "made no sense",
]);

// ── Greeting/farewell markers ──
const GREETING_MARKERS = new Set([
  "hi", "hey", "hello", "howdy", "yo", "sup", "hola", "heya",
]);
const FAREWELL_MARKERS = new Set([
  "bye", "goodbye", "goodnight", "night", "later", "cya", "see ya",
  "see you", "farewell", "peace", "gotta go",
]);

// ── Filler/procedural markers — low-semantic-content acknowledgments ──
const FILLER_MARKERS = new Set([
  "okay", "ok", "yeah", "yes", "no", "mhm", "mm", "hmm",
  "right", "sure", "alright", "well", "whatever", "meh",
  "cool", "nice", "gotcha", "uh", "um", "ah",
]);

/**
 * Classify the kernel type from cortex signals and text patterns.
 *
 * Priority order:
 *   1. Distress override (intensity + melancholy + distress words)
 *   2. Intent-based (cortex.intent.type → primary classification)
 *   3. Topic+intent refinement (dialogue.topic + dialogue.intent)
 *   4. Text-pattern (word-level and structural patterns)
 *   5. Fallback: unknown
 */
export function classifyKernelType(
  text: string,
  ctx?: ClassifyContext,
): KernelType {
  const lower = text.toLowerCase().trim();
  const words = lower.match(/[a-z']+/g) ?? [];
  const wordSet = new Set(words);

  // ── 1. Distress override ──
  // High intensity + melancholy + distress words → emotional_distress
  // This supersedes emotional_state because distress needs maximum containment
  const hasDistress = words.some(w => DISTRESS_MARKERS.has(w));
  const intensity = ctx?.intensity ?? 0;
  const melancholy = ctx?.melancholy ?? 0;
  if (hasDistress && intensity > 0.5) return "emotional_distress";
  if (hasDistress && melancholy > 0.5) return "emotional_distress";
  // Explicit distress patterns even without high cortex signals
  if (/\bI (?:want to|just want to|wanna) (?:disappear|vanish|not exist|cease|die|not be here)\b/i.test(lower)) return "emotional_distress";
  if (/\b(?:nobody|no one|no-one) (?:would |will |could )(?:care|notice|miss|remember)\b/i.test(lower)) return "emotional_distress";
  if (/\bI can't (?:do this|take this|keep going|go on|anymore)\b/i.test(lower)) return "emotional_distress";
  if (/\bnothing (?:matters|means anything|makes sense) anymore\b/i.test(lower)) return "emotional_distress";
  if (/\bI(?:'m| am) (?:not okay|not ok|not fine|not alright|not good|falling apart|breaking down|broken|numb|hurting|overwhelmed|terrified|desperate|hopeless|helpless|alone|devastated|heartbroken|grieving|in pain)\b/i.test(lower)) return "emotional_distress";
  if (/\bI don't feel (?:anything|anything anymore|much|real)\b/i.test(lower)) return "emotional_distress";
  if (/\bI feel (?:empty|hollow|nothing|numb inside|dead inside|like nothing|like I'm fading)\b/i.test(lower)) return "emotional_distress";

  // "Why did you say that" / "That made no sense" → meta_complaint
  if (/\bwhy did you (?:say|do|respond|answer) (?:that|this|it)\b/i.test(lower)) return "meta_complaint";

  // ── 2. Intent-based classification ──
  const intentType = ctx?.intentType;
  if (intentType === "greeting") return "greeting";
  if (intentType === "farewell") return "farewell";
  if (intentType === "aeterna_address") {
    // Sub-classify: complaint vs question vs identity
    if (words.some(w => META_COMPLAINT_MARKERS.has(w))) return "meta_complaint";
    return "meta_question";
  }

  // ── 3. Topic + intent refinement ──
  const topic = ctx?.topic;
  const dialogueIntent = ctx?.dialogueIntent;

  // Contradiction detection → protest (denial/minimizing)
  if (ctx?.hasContradiction && ctx?.contradictionType) {
    const ctype = ctx.contradictionType;
    if (ctype === "denial" || ctype === "minimizing") return "protest";
    if (ctype === "deflecting" || ctype === "performing") return "protest";
  }

  // Topic-based: personal + sharing → disclosure or vulnerability
  if (topic === "personal" && dialogueIntent === "sharing") {
    // Vulnerability markers: "can I tell you", "haven't told anyone", "need to say"
    if (/\b(?:can I|could I) (?:tell|say|share|confess)\b/i.test(lower)) return "vulnerability";
    if (/\bhaven't told (?:anyone|anybody|a soul|anyone else)\b/i.test(lower)) return "vulnerability";
    if (/\bI (?:need|have|should|keep meaning|'ve been meaning) to (?:tell|say|share|confess)\b/i.test(lower)) return "vulnerability";
    // Disclosure markers: "I realized", "I discovered", "I figured out"
    if (/\bI (?:realized|figured out|discovered|noticed|came to (?:terms|realize)|finally (?:understand|see|get|know|accepted|admitted))\b/i.test(lower)) return "disclosure";
    // Default personal sharing → emotional_state
    return "emotional_state";
  }

  if (topic === "personal" && dialogueIntent === "seeking_advice") return "disclosure";
  if (topic === "aeterna") return "meta_question";
  if (topic === "game" && dialogueIntent === "playing") return "playful";

  // ── 4. Text-pattern classification ──

  // Greeting patterns (even without intent classifier)
  if (GREETING_MARKERS.has(lower.replace(/[!?]+$/, ""))) return "greeting";
  if (/^(hi|hey|hello|howdy|yo|sup|what'?s up)\b/i.test(lower) && words.length <= 4) return "greeting";

  // Farewell patterns
  if (FAREWELL_MARKERS.has(lower.replace(/[!?]+$/, ""))) return "farewell";
  if (/\b(good(?:night|bye)|see you|bye|farewell|gotta go)\b/i.test(lower) && words.length <= 5) return "farewell";

  // Filler/procedural — very short, no content words
  const contentWords = words.filter(w => !FUNCTION_WORDS.has(w) && !FILLER_MARKERS.has(w));
  if (words.length <= 2 && contentWords.length === 0) return "procedural";
  if (FILLER_MARKERS.has(lower) && words.length <= 2) return "procedural";

  // Relational — "I love/miss/care/need you" patterns
  if (words.some(w => RELATIONAL_MARKERS.has(w))) {
    if (/\bI (?:love|miss|care|need|trust|cherish|adore) (?:you|her|him|them)\b/i.test(lower)) return "relational";
    if (/\byou (?:matter|mean|are important|are everything)\b/i.test(lower)) return "relational";
  }

  // Meta complaint — player frustrated with the system response
  if (/\b(?:that (?:was|made)|you(?:'re| are) (?:not|being)|this (?:is|was)) (?:terrible|awful|horrible|wrong|broken|bad|stupid|useless|frustrating|annoying|rude|not making sense|made no sense)\b/i.test(lower)) return "meta_complaint";
  if (/\b(?:doesn't make sense|not making sense|made no sense|that made no sense|terrible response|bad response)\b/i.test(lower)) return "meta_complaint";

  // Meta question — about the Aeterna's nature
  if (/\b(?:do you|can you|are you|have you)\b.*\b(?:feel|think|opinion|conscious|alive|real|dream|care|believe|understand)\b/i.test(lower)) return "meta_question";
  if (/\b(?:who|what) are you\b/i.test(lower)) return "meta_question";

  // Philosophical
  if (/\b(?:purpose|meaning|existence|consciousness|free will|why do we|what is (?:life|consciousness|reality))\b/i.test(lower)) return "philosophical";

  // Identity claims
  if (/\bI (?:always wanted|never wanted|always liked|never liked)\b/i.test(lower)) return "identity";
  if (/\bI used to be\b/i.test(lower)) return "identity";
  if (/\bI(?:'ve| have) always been\b/i.test(lower)) return "identity";
  if (/\bI(?:'m| am) proud of\b/i.test(lower)) return "identity";

  // Rumination
  if (/\bI (?:keep|can't stop) (?:thinking|wondering|turning|going back|replaying|obsessing)\b/i.test(lower)) return "rumination";
  if (/\bI(?:'ve| have) been (?:thinking about|considering|wondering about|mulling|turning over|chewing on)\b/i.test(lower)) return "rumination";

  // Regret
  if (/\bI (?:wish I|should(?:'ve| have)|regret)\b/i.test(lower)) return "regret";

  // Transition
  if (/\b(?:things|everything|life|my (?:life|world|situation)) (?:(?:is|are) (?:changing|shifting|different|moving|falling apart|falling into place)|(?:changed|shifted|moved|fell apart))\b/i.test(lower)) return "transition";
  if (/\bI(?:'m| am) (?:in|going through|entering|starting|facing) (?:a (?:new|big|huge|major|weird|strange)|some (?:kind|sort) of)/i.test(lower)) return "transition";

  // Event sharing
  if (/\b(?:my|our) (?:friend|mom|dad|sister|brother|partner|girlfriend|boyfriend|wife|husband) (?:came|visited|called|texted|got|won|passed|died|left)\b/i.test(lower)) return "event";
  if (/\bI (?:got|won|passed|failed|lost|started|finished|quit|left|moved)\b/i.test(lower) && !lower.endsWith("?")) return "event";

  // Emotional state — "I'm [emotion]" patterns
  if (/\bI(?:'m| am) (?:sad|happy|angry|lonely|scared|worried|anxious|depressed|excited|grateful|thankful|blessed|content|peaceful|calm|relieved|proud|joyful|good|great|fine|okay|ok|alright|tired|exhausted|frustrated|annoyed|confused|lost|hurt|upset|disappointed|jealous|guilty|ashamed|embarrassed|nervous|stressed|overwhelmed|numb|empty|hollow|broken|devastated|heartbroken|grieving|miserable|devastated)\b/i.test(lower)) {
    // Distinguish distress from state
    const emotionWord = lower.match(/\bI(?:'m| am) (\w+)/)?.[1];
    if (emotionWord && DISTRESS_MARKERS.has(emotionWord)) return "emotional_distress";
    // Positive emotions get their own path
    const positiveEmotions = new Set(["happy", "good", "great", "excited", "grateful", "thankful", "blessed", "content", "peaceful", "calm", "relieved", "proud", "joyful"]);
    if (emotionWord && positiveEmotions.has(emotionWord)) return "emotional_state";
    // Negative but not crisis-level
    return "emotional_state";
  }

  // Protest — "I'm fine" with contradiction, or "it doesn't matter"
  if (/\bI(?:'m| am) (?:fine|okay|ok|alright|good|great)\b/i.test(lower) && ctx?.hasContradiction) return "protest";
  if (/\b(?:it doesn't|it doesn'?t|doesn't) matter\b/i.test(lower)) return "protest";
  if (/\b(?:nothing'?s wrong|everything'?s fine|I(?:'m| am) fine)\b/i.test(lower)) return "protest";

  // Playful / hypothetical
  if (/\bwhat if I (?:just|actually)?(?:quit|left|moved|dropped|stopped|ran|disappeared|walked away|started over)\b/i.test(lower)) return "playful";

  // Factual
  if (/^(?:it(?:'s| is)|the (?:time|date|day|year|month)) (?:currently |now )?\b/i.test(lower) && !lower.endsWith("?")) return "factual";
  if (/^today is\b/i.test(lower) && !lower.endsWith("?")) return "factual";

  // Mundane — casual state sharing, low intensity
  // Guard: when no ClassifyContext is provided, intensity defaults to 0.
  // Don't classify as mundane if the text has emotional/metaphorical weight.
  // "I feel like the floor disappeared" is NOT mundane even without cortex signals.
  const hasEmotionalWeight = words.some(w => HIGH_VALUE_WORDS.has(w) || METAPHOR_WORDS.has(w))
    || ctx?.hasMetaphor === true;
  if (contentWords.length > 0 && !hasEmotionalWeight && (intensity < 0.3 || (intensity < 0.4 && melancholy < 0.3))) {
    // Short casual statements: "I'm tired", "Work was long", "Just chilling"
    if (words.length <= 6 && !lower.endsWith("?")) return "mundane";
  }

  // Narrative — longer statements that don't match other types
  if (words.length >= 6 && !lower.endsWith("?")) return "narrative";

  // ── 5. Fallback ──
  return "unknown";
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Extract semantic kernels from player text.
 *
 * For short text (<=3 words), the entire text IS the kernel.
 * For longer text, slides 2-4 word windows and scores by:
 *   - Affect intensity from lexicon
 *   - Content word density
 *   - Embedding distinctiveness from text centroid
 *
 * The optional ctx parameter provides cortex signals for kernel type
 * classification. When not provided, classification uses text patterns only
 * and defaults to "unknown" for ambiguous inputs.
 */
export function extractKernel(text: string, ctx?: ClassifyContext): KernelResult {
  const words = tokenize(text);
  const fullEmb = embedQuery(text);
  const properNouns = extractProperNounsFromText(text);

  // Short text: the whole thing is the kernel
  if (words.length <= 3) {
    const lex = scoreLexicon(words);
    let anchors = words.filter(w => !FUNCTION_WORDS.has(w)).slice(0, 2);
    let kernelWords = words;
    let kernelSpan = words.join(" ");
    // Affect-derived anchor: when no content words, seed from affect vector
    // Inject the anchor into the kernel's words/span so operators can find it
    if (anchors.length === 0 && ctx?.affectVector) {
      const affectAnchor = deriveAffectAnchor(ctx.affectVector);
      anchors = [affectAnchor];
      kernelWords = [...words, affectAnchor];
      kernelSpan = [...words, affectAnchor].join(" ");
    }
    const k: Kernel = {
      span: kernelSpan,
      words: kernelWords,
      charge: Math.max(0.3, maxDim(lex.affect)),
      embedding: fullEmb,
      position: 0.5,
    };
    return {
      primary: k,
      secondary: null,
      anchors,
      fullEmbedding: fullEmb,
      kernelType: classifyKernelType(text, ctx),
    };
  }

  // Build span candidates (windows of size 2, 3, 4)
  const spans: SpanCandidate[] = [];

  for (let size = 2; size <= Math.min(4, words.length); size++) {
    for (let i = 0; i <= words.length - size; i++) {
      const spanWords = words.slice(i, i + size);
      const cr = contentRatio(spanWords);
      if (cr < 0.25) continue; // skip all-function-word spans

      const spanText = spanWords.join(" ");
      const emb = embedQuery(spanText);
      const lex = scoreLexicon(spanWords);
      const affectIntensity = lex.coverage > 0 ? maxDim(lex.affect) : 0;

      spans.push({
        text: spanText,
        words: spanWords,
        embedding: emb,
        position: (i + size / 2) / words.length,
        startIdx: i,
        endIdx: i + size,
        charge: 0, // computed below
      });

      // Pre-store affect for scoring
      (spans[spans.length - 1] as any)._affect = affectIntensity;
      (spans[spans.length - 1] as any)._cr = cr;
    }
  }

  if (spans.length === 0) {
    // Fallback: use first 2 content words
    const content = words.filter(w => !FUNCTION_WORDS.has(w));
    let fallbackWords = content.length > 0 ? content.slice(0, 2) : words.slice(0, 2);
    let anchors = fallbackWords;
    // Affect-derived anchor: when no content words, seed from affect vector
    if (content.length === 0 && ctx?.affectVector) {
      const affectAnchor = deriveAffectAnchor(ctx.affectVector);
      anchors = [affectAnchor];
      fallbackWords = [...fallbackWords, affectAnchor];
    }
    const k: Kernel = {
      span: fallbackWords.join(" "),
      words: fallbackWords,
      charge: 0.2,
      embedding: fullEmb,
      position: 0.5,
    };
    return { primary: k, secondary: null, anchors, fullEmbedding: fullEmb, kernelType: classifyKernelType(text, ctx) };
  }

  // Compute text centroid for distinctiveness scoring
  const centroid = avgEmbedding(spans.map(s => s.embedding));

  // Idiomatic intensifier detection (constant per text — hoisted out of loop).
  // "best sandwich of my life" → "life" is an intensifier, not content.
  const isIdiomatic = RE_IDIOMATIC_INTENSIFIER.test(text);
  const idiomaticTailWords = isIdiomatic
    ? new Set(text.toLowerCase().match(RE_IDIOMATIC_INTENSIFIER)?.[0].split(/\s+/) ?? [])
    : null;

  // Idiomatic casual phrase detection (constant per text — hoisted out of loop).
  // "from scratch" → "scratch" is not content. "not gonna lie" → "lie" is not content.
  const hasIdiomaticCasual = RE_IDIOMATIC_CASUAL_PHRASE.test(text);

  // Score each span
  for (const span of spans) {
    const distinct = 1 - Math.max(0, cosine(span.embedding, centroid));
    const affect = (span as any)._affect as number;
    const cr = (span as any)._cr as number;
    // Triple product: semantically distinctive × emotionally charged × content-dense
    let charge = affect * 0.45 + distinct * 0.30 + cr * 0.25;

    // High-value word bonus: ensure emotionally loaded spans outrank function-
    // word-adjacent ones even when affect-lexicon coverage is zero (e.g. "the
    // floor just disappeared").
    // EXCEPTION: idiomatic intensifiers — don't bonus "life" in "of my life",
    // and actively DEMOTE it. The player is talking about the sandwich, not
    // about life. The idiomatic tail words should score BELOW content words.
    const hasHighValue = span.words.some(w =>
      HIGH_VALUE_WORDS.has(w) && !(idiomaticTailWords?.has(w))
    );
    if (hasHighValue) charge += 0.2;
    // Idiomatic intensifier demotion: words in the idiomatic tail ("life" in
    // "of my life", "time" in "of all time") are intensifiers, not content.
    // Demote them so the actual content word ("sandwich") outranks them.
    if (idiomaticTailWords) {
      const idiomaticInSpan = span.words.filter(w => idiomaticTailWords.has(w));
      if (idiomaticInSpan.length > 0) charge -= 0.25 * (idiomaticInSpan.length / span.words.length);
    }

    // Idiomatic casual phrase demotion: "scratch" in "from scratch", "lie" in
    // "not gonna lie" carry ZERO content meaning. Flat -0.30 demotion when any
    // such word appears in the span — proportional demotion was too weak for
    // short spans where the idiomatic word is 1 of 4 words.
    if (hasIdiomaticCasual) {
      const hasIdiomaticWord = span.words.some(w => IDIOMATIC_CASUAL_PHRASE_WORDS.has(w));
      if (hasIdiomaticWord) charge -= 0.30;
    }

    // Metaphor bonus: spatial/physical metaphors are morphologically rich —
    // give them an additional edge so the operator has the best possible seed.
    const hasMetaphor = span.words.some(w => METAPHOR_WORDS.has(w));
    if (hasMetaphor) charge += 0.15;

    // Relational bonus: spans mentioning a person carry emotional weight
    // that generic spans don't — "miss her" outranks "miss silence".
    const hasRelational = span.words.some(w => RELATIONAL_WORDS.has(w));
    if (hasRelational) charge += 0.15;

    // Proper noun demotion: names shouldn't become kernels.
    const properCount = span.words.filter(w => properNouns.has(w)).length;
    if (properCount > 0) charge -= 0.25 * (properCount / span.words.length);

    // Concrete object demotion: mundane physical objects ("toilet", "mug",
    // "glasses", "phone", "car", "table") make terrible emotional anchors.
    // "The weight of toilet" is absurd. Demote spans dominated by these.
    // Reduced from -0.20 to -0.12: aggressive demotion killed the only content
    // word in casual messages ("best sandwich of my life" → all demoted → hollow).
    const concreteCount = span.words.filter(w => CONCRETE_OBJECTS.has(w)).length;
    if (concreteCount > 0) charge -= 0.12 * (concreteCount / span.words.length);

    // Light action verb demotion: generic physical actions that describe
    // WHAT happened but not WHY it matters. In casual stories, nouns
    // should outrank verbs as kernels.
    // Idiomatic casual words ("scratch", "vibes", "lie") get heavier demotion
    // because they're almost always used idiomatically, not as content.
    const lightVerbCount = span.words.filter(w => LIGHT_ACTION_VERBS.has(w)).length;
    if (lightVerbCount > 0) {
      const idiomaticCount = span.words.filter(w => IDIOMATIC_CASUAL.has(w)).length;
      const demotion = idiomaticCount > 0 ? 0.15 : 0.10;
      charge -= demotion * (lightVerbCount / span.words.length);
    }

    // Number/quantity demotion: "twelve", "six", "hundred" have no emotional charge
    const numCount = span.words.filter(w => NUMBERS.has(w) || /^\d+$/.test(w)).length;
    if (numCount > 0) charge -= 0.20 * (numCount / span.words.length);

    // Academic/technical demotion: "neurons", "algorithm", "quantum" etc.
    // carry zero emotional weight — "the weight of neurons" is absurd.
    const acadCount = span.words.filter(w => ACADEMIC_WORDS.has(w)).length;
    if (acadCount > 0) charge -= 0.20 * (acadCount / span.words.length);

    span.charge = charge;
  }

  // Sort by charge (descending)
  spans.sort((a, b) => b.charge - a.charge);

  const primary = spans[0];
  // For rich input (10+ words), lower the secondary threshold to capture more content.
  // The manifesto demands that we don't discard 90% of what the player shared.
  // A single kernel from a 30-word disclosure is lossy extraction.
  const secondaryThreshold = words.length > 10 ? 0.10 : 0.15;
  const secondary = spans.find(s => !overlaps(s, primary) && s.charge > secondaryThreshold) ?? null;

  // Extract individual anchor words (content words by affect intensity)
  const anchorScores: Array<[string, number]> = [];
  for (const w of words) {
    if (FUNCTION_WORDS.has(w)) continue;
    // Proper nouns are not anchors — skip them entirely
    if (properNouns.has(w)) continue;
    const lex = scoreLexicon([w]);
    let score = lex.coverage > 0 ? maxDim(lex.affect) : 0.1;
    // HIGH_VALUE_WORDS bonus: these words are semantically anchoring even when
    // the affect lexicon has no entry for them (e.g. "floor", "disappeared").
    if (HIGH_VALUE_WORDS.has(w)) score += 0.3;
    // Demote non-emotional categories from anchor consideration
    if (CONCRETE_OBJECTS.has(w)) score -= 0.20;
    if (NUMBERS.has(w) || /^\d+$/.test(w)) score -= 0.20;
    if (ACADEMIC_WORDS.has(w)) score -= 0.20;
    anchorScores.push([w, score]);
  }
  anchorScores.sort((a, b) => b[1] - a[1]);
  const anchors = anchorScores.slice(0, 3).map(([w]) => w);

  // For very rich input (15+ words), try to find a tertiary kernel too.
  // This adds additional anchor words, giving the planner more material.
  // Without it, a 30-word disclosure about grief, loss, and the passage
  // of time gets reduced to a single 2-word span.
  if (words.length > 15 && secondary) {
    const tertiary = spans.find(s =>
      !overlaps(s, primary) && !overlaps(s, secondary) && s.charge > 0.08
    );
    if (tertiary) {
      const tertiaryContent = tertiary.words.filter(w => !FUNCTION_WORDS.has(w) && !properNouns.has(w));
      for (const w of tertiaryContent) {
        if (!anchors.includes(w)) anchors.push(w);
      }
    }
  }

  // Affect-derived anchor: when no anchors found, seed from affect vector
  let finalAnchors = anchors.length > 0 ? anchors : [words[0]];
  if (anchors.length === 0 && ctx?.affectVector) {
    finalAnchors = [deriveAffectAnchor(ctx.affectVector)];
  }

  return {
    primary: toKernel(primary),
    secondary: secondary ? toKernel(secondary) : null,
    anchors: finalAnchors,
    fullEmbedding: fullEmb,
    kernelType: classifyKernelType(text, ctx),
  };
}

function toKernel(s: SpanCandidate): Kernel {
  return {
    span: s.text,
    words: s.words,
    charge: s.charge,
    embedding: s.embedding,
    position: s.position,
  };
}

/**
 * Detect whether the player is talking about a specific person.
 * Returns the relational word ("mom", "her", etc.) or null.
 */
export function detectRelationalContext(text: string): string | null {
  const words = tokenize(text);
  // "my X" patterns for named relationships
  for (let i = 0; i < words.length - 1; i++) {
    if (words[i] === "my" && RELATIONAL_WORDS.has(words[i + 1])) {
      return words[i + 1];
    }
  }
  // Standalone relational pronouns in grief/loss context
  const lossMarkers = /\b(passed|died|miss|lost|gone|remember|dream)\b/i;
  if (lossMarkers.test(text)) {
    if (words.includes("her")) return "her";
    if (words.includes("him")) return "him";
    if (words.includes("them")) return "them";
  }
  return null;
}
